/**
 * Which editor owns the document a route is showing.
 *
 * The document route used to mount the Lexical editor for everything, so a
 * shared spreadsheet opened as an empty rich-text document. This resolves the
 * document's index row against the live document-type catalog and hands the
 * route the same verdict the tree and the list already show — `ready` with a
 * descriptor, or `unsupported` with a reason.
 *
 * The index row is read from the Shared Docs session's data source rather than
 * from its atoms, for the reason `useSharedDocumentTitles` documents: the atoms
 * belong to the bundle's Jotai instance and a host reading them through its own
 * import gets a second, empty store.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getCollabDocsSession,
  resolveSharedDocumentTypePresentation,
  type SharedDocument,
  type SharedDocumentTypePresentation,
} from '@nimbalyst/collab-bundle/docs-ui';
import { getBrowserExtensionsRevision, onBrowserExtensionsChanged } from '@nimbalyst/web-editors/registry';
import { webCollabScopeKey } from './WebCollabHost';
import { webDocumentTypeDescriptors } from './webDocumentTypeCatalog';
import { WebCollabDocumentsDataSource } from './WebCollabDocumentsDataSource';

export type SharedDocumentTypeResolution =
  /** A session is open and its index has not answered yet. */
  | { state: 'pending' }
  /**
   * No verdict is available: either there is no Shared Docs session (the
   * standalone editor harness routes), or the index has synced and holds no row
   * for this document. Callers must fall back to the default editor rather than
   * read this as "unsupported" — it is the absence of an answer, not a no.
   */
  | { state: 'unknown' }
  | { state: 'resolved'; document: SharedDocument; presentation: SharedDocumentTypePresentation };

function documentsDataSource(scopeKey: string): WebCollabDocumentsDataSource | null {
  const dataSource = getCollabDocsSession(scopeKey)?.dataSource;
  return dataSource instanceof WebCollabDocumentsDataSource ? dataSource : null;
}

interface IndexLookup {
  /** Identity of the route whose row this lookup contains. */
  requestKey: string;
  row: SharedDocument | null;
  /** True once the index has produced a snapshot, so a missing row is final. */
  settled: boolean;
}

/**
 * Track one document's index row, and whether the index has answered at all.
 *
 * Both halves matter. A missing row before the index syncs means "wait"; the
 * same missing row after it syncs means this project's index does not hold the
 * document, and the route has to fall back rather than stall.
 */
function useSharedDocumentIndexRow(scopeKey: string, documentId: string): IndexLookup {
  const requestKey = `${scopeKey.length}:${scopeKey}${documentId}`;
  const read = useCallback((): IndexLookup => {
    const dataSource = documentsDataSource(scopeKey);
    if (!dataSource) return { requestKey, row: null, settled: true };
    return {
      requestKey,
      row: dataSource.getDocument(documentId),
      // A reconnecting source has already delivered a snapshot once, so its
      // rows stay authoritative rather than reverting the route to "opening".
      settled: dataSource.status() === 'connected' || dataSource.getDocument(documentId) !== null,
    };
  }, [documentId, requestKey, scopeKey]);
  const [lookup, setLookup] = useState<IndexLookup>(read);

  useEffect(() => {
    // Held to the same value when nothing about this document changed: the
    // source emits on every index broadcast in the project, and a fresh object
    // per event would re-render the open document for a rename three folders
    // away.
    const apply = (next: IndexLookup) => setLookup((current) => (
      current.requestKey === next.requestKey
        && current.row === next.row
        && current.settled === next.settled
        ? current
        : next
    ));
    apply(read());
    const dataSource = documentsDataSource(scopeKey);
    if (!dataSource) return;
    // Every change is a re-read rather than a patch: the row can arrive in the
    // initial snapshot, in a broadcast, or in a resync after a refused
    // mutation, and only the source knows which of those it just applied.
    return dataSource.subscribe(() => apply(read()));
  }, [read, scopeKey]);

  // Effects run after render. On a route switch, React state therefore still
  // contains the previous document's row for one render. Returning that row is
  // not merely cosmetic: `useSharedDocumentType` can latch its ready editor
  // under the new document id, then retain Canvas when the real destination is
  // unsupported. Read the new identity synchronously for this one transition;
  // the effect above adopts the same lookup immediately after commit.
  return lookup.requestKey === requestKey ? lookup : read();
}

export function useSharedDocumentType(
  orgId: string,
  projectId: string,
  documentId: string,
): SharedDocumentTypeResolution {
  const scopeKey = webCollabScopeKey(orgId, projectId);
  const { row, settled } = useSharedDocumentIndexRow(scopeKey, documentId);
  const revision = useBrowserExtensionsRevision();
  /**
   * The verdict this document has already been opened with.
   *
   * The route mounts the editor off this resolution, so a verdict that changes
   * under an already-open document does not re-label anything — it swaps the
   * editor for a panel, and the live editor is destroyed behind a teardown
   * flush. That is not hypothetical: the index re-broadcasts a row on every
   * content edit, and a broadcast row carrying an encrypted title resolves as
   * `unsupported` where the initial sync row resolved as `ready`. Editing one
   * cell of a shared spreadsheet took the spreadsheet off the screen.
   *
   * So `ready` latches per document. A row can still go from pending to ready,
   * and switching documents resets it; what it cannot do is retract an editor
   * this route has already handed a live Y.Doc to. A document that genuinely
   * goes away is reported by the room as a termination, not by this verdict.
   */
  const latched = useRef<{ documentId: string; presentation: SharedDocumentTypePresentation } | null>(null);
  if (latched.current && latched.current.documentId !== documentId) latched.current = null;

  return useMemo(() => {
    if (!row) return { state: settled ? ('unknown' as const) : ('pending' as const) };
    const resolved = resolveSharedDocumentTypePresentation(row, webDocumentTypeDescriptors());
    if (resolved.state === 'ready') latched.current = { documentId, presentation: resolved };
    const presentation = latched.current?.documentId === documentId
      ? latched.current.presentation
      : resolved;
    return { state: 'resolved' as const, document: row, presentation };
    // `revision` is a dependency because the catalog it feeds is rebuilt when a
    // pinned bundle finishes loading or fails: the same row can go from openable
    // to explained-as-broken without the row itself changing.
  }, [documentId, revision, row, settled]);
}

function useBrowserExtensionsRevision(): number {
  const [revision, setRevision] = useState(getBrowserExtensionsRevision);
  const sync = useCallback(() => setRevision(getBrowserExtensionsRevision()), []);
  useEffect(() => {
    sync();
    return onBrowserExtensionsChanged(sync);
  }, [sync]);
  return revision;
}
